import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Link } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";

import { forgotPassword } from "../slices/auth";
import { clearMessage } from "../slices/message";
import { MdOutlineMailLock } from "react-icons/md";

import logo from "../assets/images/LogoMercadito.png";
import { IoArrowBackCircleOutline } from "react-icons/io5";

import { AppDispatch, RootState } from "../store";

interface ForgotPasswordFormValues {
  username: string;
}

const ForgotPassword: React.FC = () => {
  const [successful, setSuccessful] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch: AppDispatch = useDispatch();
  const { isLoggedIn } = useSelector((state: RootState) => state.auth);
  const { message } = useSelector((state: RootState) => state.message);

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const initialValues: ForgotPasswordFormValues = {
    username: "",
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("¡Nombre de usuario requerido!"),
  });

  const handleForgotPassword = (
    values: ForgotPasswordFormValues,
    { setSubmitting }: FormikHelpers<ForgotPasswordFormValues>
  ) => {
    const { username } = values;
    setLoading(true);
    setSuccessful(false);

    dispatch(clearMessage());

    dispatch(forgotPassword({ username }))
      .unwrap()
      .then(() => {
        setLoading(false);
        setSuccessful(true);
      })
      .catch(() => {
        setLoading(false);
        setSuccessful(false);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (isLoggedIn) {
    return <Navigate to="/home" />;
  }

  return (
    <div className="main-wrapper">
      <img src={logo} alt="casa26-logo" className="logo-img-card" />
      <div className="col-md-12 forgot-password-form">
        <div className="card card-container">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleForgotPassword}
          >
            <Form>
              <div className="form-group">
                <label className="forgot-password-label">
                  Por favor, escriba su nombre de usuario para recuperar su contraseña
                </label>
                <div className="wrapper">
                  <MdOutlineMailLock className="icon" />
                  <Field
                    name="username"
                    type="text"
                    placeholder="Nombre de usuario"
                    className="form-control input"
                  />
                </div>
                <ErrorMessage
                  name="username"
                  component="div"
                  className="alert alert-danger"
                />
              </div>

              <div className="form-group">
                <button
                  type="submit"
                  className="btn btn-secondary btn-block mt-2"
                  disabled={loading}
                >
                  {loading && (
                    <span className="spinner-border spinner-border-sm"></span>
                  )}
                  <span>Recuperar contraseña</span>
                </button>
              </div>
            </Form>
          </Formik>
          <Link to={"/login"} className="forgot-password-link mt-3">
            <IoArrowBackCircleOutline className="back-icon" /> Iniciar Sesión
          </Link>
        </div>

        {message && (
          <div className="form-group">
            <div
              className={
                successful ? "alert alert-success" : "alert alert-danger"
              }
              role="alert"
            >
              {message}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
